import { GRID_LAYERS } from "./GridModel.js";
import { BLANK_CHAR } from "./GridTile.js";

export class GameView {
  /**
   * @param {HTMLCanvasElement} canvas - The canvas element to render the grid on.
   */
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    // cellSize will be calculated dynamically based on canvas size
    this.cellSize = 30;
    this.fontSizeStr = `${this.cellSize * 0.6}px sans-serif`;
    this.responsiveMode = "desktop"; // Track current mode
  }

  /**
   * Calculate optimal cell size based on canvas dimensions and grid size.
   * Ensures cells remain square and fit within available space.
   * @param {number} rows - Number of rows in grid
   * @param {number} cols - Number of columns in grid
   */
  calculateCellSize(rows, cols) {
    // Get the parent container's dimensions
    const container = this.canvas.parentElement;
    const containerWidth = container.clientWidth - 30; // Account for padding
    const containerHeight = container.clientHeight - 30;

    // Calculate cell size to fit the grid while keeping cells square
    const cellWidth = Math.floor(containerWidth / cols);
    const cellHeight = Math.floor(containerHeight / rows);

    // Use the smaller dimension to ensure grid fits in both directions
    const cellSize = Math.min(cellWidth, cellHeight);

    // Apply responsive constraints based on mode
    let minSize, maxSize;
    switch (this.responsiveMode) {
      case "mobile":
        minSize = 15;
        maxSize = 25;
        break;
      case "tablet":
        minSize = 20;
        maxSize = 30;
        break;
      case "desktop":
      default:
        minSize = 30;
        maxSize = 40;
        break;
    }

    // Clamp cell size within responsive constraints
    this.cellSize = Math.max(minSize, Math.min(maxSize, cellSize));
    this.fontSizeStr = `${this.cellSize * 0.6}px sans-serif`;

    // console.log(`[GameView] Calculated cellSize: ${this.cellSize}px (${rows}x${cols} grid, ${this.responsiveMode} mode)`);
  }

  // Example: inside your render function or before rendering
  getTileLetter(tile, theLayer) {
    if (!tile) return "";
    switch (theLayer) {
      case GRID_LAYERS.SOLUTION:
        return tile.getSolutionValue();
      case GRID_LAYERS.PLAYING:
        return tile.getPlayingValue();
      case GRID_LAYERS.PEEKING:
        return tile.getPeekingValue();
      case GRID_LAYERS.SCRATCH:
        return tile.getScratchValue();
      default:
        return "";
    }
  }

  drawSelectedTile(selectedTile) {
    if (selectedTile) {
      const x = selectedTile.col * this.cellSize;
      const y = selectedTile.row * this.cellSize;

      this.ctx.fillStyle = "#F9DB4A";
      this.ctx.fillRect(x, y, this.cellSize, this.cellSize);
      this.ctx.strokeStyle = "#000";
      this.ctx.strokeRect(x, y, this.cellSize, this.cellSize);

      let letter = selectedTile.getPlayingValue();
      this.ctx.fillStyle = "#222";
      this.ctx.font = `${this.cellSize * 0.6}px sans-serif`;
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";
      this.ctx.fillText(letter, x + this.cellSize / 2, y + this.cellSize / 2);
      const peekletter = selectedTile.getPeekingValue();
      if (letter == peekletter) {
        if (letter !== BLANK_CHAR) {
          this.drawPeekedBadge(x, y);
        }
      }
    }
  }

  drawSelectedWord(selectedWord) {
    if (!selectedWord || typeof selectedWord.getTiles !== "function") return;
    const tiles = selectedWord.getTiles();
    for (const tile of tiles) {
      if (!tile) continue;
      const x = tile.col * this.cellSize;
      const y = tile.row * this.cellSize;

      // Highlight the word tile
      this.ctx.fillStyle = "#B1D7FB";
      this.ctx.fillRect(x, y, this.cellSize, this.cellSize);
      this.ctx.strokeStyle = "#000";
      this.ctx.strokeRect(x, y, this.cellSize, this.cellSize);

      // Draw the letter for this tile at the given layer
      const letter = tile.getPlayingValue();
      const peekletter = tile.getPeekingValue();
      this.ctx.fillStyle = "#222";
      this.ctx.font = `${this.cellSize * 0.6}px sans-serif`;
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";
      this.ctx.fillText(letter, x + this.cellSize / 2, y + this.cellSize / 2);
      if (letter == peekletter) {
        if (letter !== BLANK_CHAR) {
          this.drawPeekedBadge(x, y);
        }
      }
    }
  }

  /**
   * Quick render grid matrix playing layer on the canvas.
   * @param {Array<Array<GridTile|null>>} matrix - The grid to render.
   * @param {GridTile} selectedTile - currently selected tile
   * @param {ORIENTATION} orientation - the current word orientation
   */
  render(matrix, selectedTile, orientation) {
    if (!Array.isArray(matrix) || matrix.length === 0) return;
    const rows = matrix.length;
    const cols = matrix[0].length;

    // Calculate optimal cell size based on canvas container and grid dimensions
    this.calculateCellSize(rows, cols);

    // Set canvas size based on calculated cell size (cells remain square)
    this.canvas.width = cols * this.cellSize;
    this.canvas.height = rows * this.cellSize;

    // Draw grid
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = c * this.cellSize;
        const y = r * this.cellSize;

        // Draw cell background
        this.ctx.fillStyle = "#fff";
        this.ctx.fillRect(x, y, this.cellSize, this.cellSize);
        this.ctx.strokeStyle = "#000";
        this.ctx.strokeRect(x, y, this.cellSize, this.cellSize);

        // Draw letter if present
        const tile = matrix[r][c];
        if (tile && typeof tile === "object") {
          let letter = tile.getPlayingValue();
          let peekletter = tile.getPeekingValue();
          this.ctx.fillStyle = "#222";
          this.ctx.font = this.fontSizeStr;
          this.ctx.textAlign = "center";
          this.ctx.textBaseline = "middle";
          this.ctx.fillText(
            letter,
            x + this.cellSize / 2,
            y + this.cellSize / 2
          );
          if (letter == peekletter) {
            if (letter !== BLANK_CHAR) {
              this.drawPeekedBadge(x, y);
            }
          }
        } else {
          this.ctx.fillStyle = "#000";
          this.ctx.fillRect(x, y, this.cellSize, this.cellSize);
        }
      }
    }

    // Draw selected word and tile
    if (selectedTile) {
      let selectedWord = null;
      if (orientation === "ACROSS") {
        selectedWord = selectedTile.getAcrossWord();
      } else {
        selectedWord = selectedTile.downGridWord;
      }
      this.drawSelectedWord(selectedWord, GRID_LAYERS.PLAYING);
      this.drawSelectedTile(selectedTile);
    }
  }

  drawPeekedBadge(x, y) {
    this.ctx.fillStyle = "darkred"; // dark red
    this.ctx.beginPath();
    this.ctx.moveTo(x + 1, y + 1); // top-left
    this.ctx.lineTo(x + 8, y + 1); // top-right
    this.ctx.lineTo(x + 1, y + 8); // bottom-left
    this.ctx.closePath();
    this.ctx.fill();
  }

  setResponsiveMode(mode) {
    console.log("[GameView] setResponsiveMode:", mode);
    this.responsiveMode = mode;
    // Cell size will be recalculated on next render() call
  }
}
